import {CommonModule} from '@angular/common';
import {Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HubProcessEditorScreen, HubProcessEditorWorkflow} from '../../../model/hub-session';
import {HelpTooltipComponent} from '../../../components/help-tooltip/help-tooltip.component';
import {HubApiService} from '../../../services/hub-api.service';
import '@web.awesome.me/webawesome-pro/dist/components/icon/icon.js';
type WorkflowMode = 'gui' | 'json';
type WorkflowNodeKind = 'task' | 'event' | 'gateway';
type WorkflowAttachment = 'top' | 'right' | 'bottom' | 'left';
type WorkflowConnectionEndpoint = 'from' | 'to';
type WorkflowConnectionType = 'forward' | 'back' | 'submit' | 'script' | 'default';

interface WorkflowPosition {
  x: number;
  y: number;
}

interface WorkflowTask {
  id: string;
  name: string;
  progress: string;
  taskType: string;
  screen: string;
  pos: WorkflowPosition;
}

interface WorkflowEvent {
  id: string;
  eventType: 'start' | 'end';
  pos: WorkflowPosition;
}

interface WorkflowGateway {
  id: string;
  type: string;
  pos: WorkflowPosition;
}

interface WorkflowConnection {
  id: string;
  from: string;
  to: string;
  fromAttachment: WorkflowAttachment;
  toAttachment: WorkflowAttachment;
  connectionType: WorkflowConnectionType;
  connectionScript: string;
  enabledCondition: string;
}

interface WorkflowModel {
  tasks: WorkflowTask[];
  events: WorkflowEvent[];
  gateways: WorkflowGateway[];
  connections: WorkflowConnection[];
}

interface WorkflowNodeRef {
  id: string;
  kind: WorkflowNodeKind;
  item: WorkflowTask | WorkflowEvent | WorkflowGateway;
}

interface WorkflowNodeBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ConnectionGeometry {
  start: WorkflowPosition;
  startControl: WorkflowPosition;
  endControl: WorkflowPosition;
  end: WorkflowPosition;
  path: string;
  arrowPath: string;
  label: WorkflowPosition;
}

interface WorkflowConnectionEndpointRef {
  connectionId: string;
  endpoint: WorkflowConnectionEndpoint;
  axis: number;
}

interface DragState {
  primaryNodeId: string;
  nodeIds: string[];
  startPointer: WorkflowPosition;
  startNodes: Record<string, WorkflowPosition>;
}

interface SelectionBoxState {
  start: WorkflowPosition;
  current: WorkflowPosition;
  additive: boolean;
  moved: boolean;
}

interface SnapGuides {
  vertical: number | null;
  horizontal: number | null;
}

interface ConnectionDragState {
  connectionId?: string;
  endpoint: WorkflowConnectionEndpoint;
  fixedNodeId: string;
  pointer: WorkflowPosition;
  attachment: WorkflowAttachment;
}

interface WorkflowEditorChange {
  json: string;
  debounce?: boolean;
}

interface JsonEditorError {
  title: string;
  details: string[];
}

@Component({
  selector: 'hub-workflow-editor',
  imports: [CommonModule, FormsModule, HelpTooltipComponent],
  templateUrl: './workflow-editor.component.html',
  styleUrl: './workflow-editor.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WorkflowEditorComponent implements OnChanges {
  @Input({required: true}) workflow!: HubProcessEditorWorkflow;
  @Input() screens: HubProcessEditorScreen[] = [];
  @Input() canUndo = false;
  @Input() canRedo = false;
  @Input() autosaveStatus = '';
  @Output() workflowChange = new EventEmitter<WorkflowEditorChange>();
  @Output() undoRequested = new EventEmitter<void>();
  @Output() redoRequested = new EventEmitter<void>();

  @ViewChild('canvas', {static: false}) canvas?: ElementRef<SVGSVGElement>;

  readonly taskSize = {width: 168, height: 76};
  readonly eventSize = {width: 62, height: 62};
  readonly gatewaySize = {width: 74, height: 74};
  readonly nodeInnerInset = 8;
  readonly minZoom = 0.2;
  readonly maxZoom = 2;
  readonly zoomStep = 0.1;
  readonly snapThresholdPixels = 10;
  readonly connectionEndpointSpacing = 18;
  readonly connectionEndpointPadding = 14;
  readonly connectionTypes: Array<{ value: WorkflowConnectionType; labelKey: string }> = [
    {value: 'default', labelKey: 'HubWorkflowConnectionDefault'},
    {value: 'forward', labelKey: 'HubWorkflowConnectionForward'},
    {value: 'back', labelKey: 'HubWorkflowConnectionBack'},
    {value: 'submit', labelKey: 'HubWorkflowConnectionSubmit'},
    {value: 'script', labelKey: 'HubWorkflowConnectionTypeScript'}
  ];
  readonly taskTypes = ['php', 'json'];

  model: WorkflowModel = this.emptyModel();
  mode: WorkflowMode = 'gui';
  parseError: JsonEditorError | null = null;
  visualReady = true;
  selectedId = '';
  selectedIds = new Set<string>();
  zoom = 1;
  snapGuides: SnapGuides = {vertical: null, horizontal: null};
  rawDirty = false;

  private dragState: DragState | null = null;
  private connectionDragState: ConnectionDragState | null = null;
  private selectionBoxState: SelectionBoxState | null = null;

  constructor(private hubApi: HubApiService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflow']) {
      this.parseWorkflowJson(this.workflow?.json || this.defaultWorkflowJson());
    }
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    if (this.dragState) this.continueNodeDrag(event);
    if (this.connectionDragState) this.continueConnectionDrag(event);
    if (this.selectionBoxState) this.continueSelectionBox(event);
  }

  @HostListener('document:pointerup', ['$event'])
  onDocumentPointerUp(event: PointerEvent): void {
    if (this.dragState) this.finishNodeDrag();
    if (this.connectionDragState) this.finishConnectionDrag(event);
    if (this.selectionBoxState) this.finishSelectionBox();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const tagName = target?.tagName?.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return;
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      this.selectAll();
      return;
    }
    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    if (!this.hasSelection()) return;
    event.preventDefault();
    this.deleteSelected();
  }

  t(key: string): string {
    return this.hubApi.translate(key) || key;
  }

  toggleMode(): void {
    if (this.mode === 'gui') {
      this.syncJson();
      this.mode = 'json';
      return;
    }

    this.applyJson();
    if (!this.parseError) this.mode = 'gui';
  }

  applyJson(markDirty = true): void {
    const wasRawDirty = this.rawDirty;
    this.parseWorkflowJson(this.workflow.json, this.mode);
    if (!this.parseError) {
      this.rawDirty = false;
      this.syncJson();
      if (markDirty && wasRawDirty) this.emitWorkflowChange();
    }
  }

  onJsonChange(): void {
    this.rawDirty = true;
    this.emitWorkflowChange(true);
  }

  requestUndo(): void {
    if (!this.canUndo) return;
    this.undoRequested.emit();
  }

  requestRedo(): void {
    if (!this.canRedo) return;
    this.redoRequested.emit();
  }

  addTask(): void {
    const task: WorkflowTask = {
      id: this.uniqueId('task'),
      name: this.t('HubWorkflowNewTask'),
      progress: '0',
      taskType: 'json',
      screen: '',
      pos: this.nextPosition()
    };
    this.model.tasks.push(task);
    this.setSingleSelection(task.id);
    this.syncJson();
    this.emitWorkflowChange();
  }

  addEvent(eventType: 'start' | 'end'): void {
    const event: WorkflowEvent = {
      id: this.uniqueId('event'),
      eventType,
      pos: this.nextPosition()
    };
    this.model.events.push(event);
    this.setSingleSelection(event.id);
    this.syncJson();
    this.emitWorkflowChange();
  }

  addGateway(): void {
    const gateway: WorkflowGateway = {
      id: this.uniqueId('gateway'),
      type: 'exclusive',
      pos: this.nextPosition()
    };
    this.model.gateways.push(gateway);
    this.setSingleSelection(gateway.id);
    this.syncJson();
    this.emitWorkflowChange();
  }

  startNodeDrag(event: PointerEvent, node: WorkflowNodeRef): void {
    if (event.button !== 0) return;
    if ((event.target as Element | null)?.closest('.connection-target')) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.shiftKey) {
      this.toggleSelection(node.id);
      return;
    }
    if (!this.selectedIds.has(node.id)) this.setSingleSelection(node.id);
    const nodeIds = this.selectedNodeIds();
    const startNodes: Record<string, WorkflowPosition> = {};
    for (const id of nodeIds) {
      const selectedNode = this.findNode(id);
      if (selectedNode) startNodes[id] = {...selectedNode.item.pos};
    }
    this.dragState = {
      primaryNodeId: node.id,
      nodeIds,
      startPointer: this.pointerPosition(event),
      startNodes
    };
  }

  startNewConnection(event: PointerEvent, node: WorkflowNodeRef, attachment: WorkflowAttachment): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.setSingleSelection(node.id);
    this.connectionDragState = {
      endpoint: 'to',
      fixedNodeId: node.id,
      pointer: this.pointerPosition(event),
      attachment
    };
  }

  startReattachConnection(event: PointerEvent, connection: WorkflowConnection, endpoint: WorkflowConnectionEndpoint): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.setSingleSelection(connection.id);
    const fixedNodeId = endpoint === 'from' ? connection.to : connection.from;
    this.connectionDragState = {
      connectionId: connection.id,
      endpoint,
      fixedNodeId,
      pointer: this.pointerPosition(event),
      attachment: endpoint === 'from' ? connection.toAttachment : connection.fromAttachment
    };
  }

  selectElement(id: string, event?: PointerEvent): void {
    event?.stopPropagation();
    if (event?.shiftKey) {
      this.toggleSelection(id);
      return;
    }
    this.setSingleSelection(id);
  }

  startSelectionBox(event: PointerEvent): void {
    if (event.button !== 0) return;
    if (event.target !== this.canvas?.nativeElement) return;
    event.preventDefault();
    const pointer = this.pointerPosition(event);
    this.selectionBoxState = {
      start: pointer,
      current: pointer,
      additive: event.shiftKey,
      moved: false
    };
    if (!event.shiftKey) this.clearSelection();
  }

  clearSelection(event?: PointerEvent): void {
    if (event?.shiftKey) return;
    this.selectedId = '';
    this.selectedIds.clear();
  }

  selectAll(): void {
    this.selectedIds = new Set([
      ...this.nodes().map((node) => node.id),
      ...this.model.connections.map((connection) => connection.id)
    ]);
    this.syncPrimarySelection();
  }

  selectedNode(): WorkflowNodeRef | null {
    return this.nodes().find((node) => node.id === this.selectedId) || null;
  }

  selectedTask(): WorkflowTask | null {
    return this.model.tasks.find((task) => task.id === this.selectedId) || null;
  }

  selectedEvent(): WorkflowEvent | null {
    return this.model.events.find((event) => event.id === this.selectedId) || null;
  }

  selectedGateway(): WorkflowGateway | null {
    return this.model.gateways.find((gateway) => gateway.id === this.selectedId) || null;
  }

  selectedConnection(): WorkflowConnection | null {
    return this.model.connections.find((connection) => connection.id === this.selectedId) || null;
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id) || this.previewSelectedIds().has(id);
  }

  hasSelection(): boolean {
    return this.selectedIds.size > 0;
  }

  isMultiSelection(): boolean {
    return this.selectedIds.size > 1;
  }

  selectionBox(): WorkflowNodeBox | null {
    const box = this.selectionBoxState;
    if (!box?.moved) return null;
    return this.rectFromPoints(box.start, box.current);
  }

  previewSelectedIds(): Set<string> {
    const box = this.selectionBox();
    if (!box || !this.selectionBoxState) return new Set();
    const preview = new Set<string>();
    if (this.selectionBoxState.additive) {
      for (const id of this.selectedIds) preview.add(id);
    }
    for (const node of this.nodes()) {
      if (this.rectsIntersect(box, this.nodeBox(node))) preview.add(node.id);
    }
    return preview;
  }

  selectionStats(): Array<{ label: string; value: number }> {
    const ids = this.selectedIds;
    const tasks = this.model.tasks.filter((task) => ids.has(task.id)).length;
    const events = this.model.events.filter((event) => ids.has(event.id)).length;
    const gateways = this.model.gateways.filter((gateway) => ids.has(gateway.id)).length;
    const connections = this.model.connections.filter((connection) => ids.has(connection.id)).length;
    return [
      {label: this.t('HubWorkflowSelectionTasks'), value: tasks},
      {label: this.t('HubWorkflowSelectionEvents'), value: events},
      {label: this.t('HubWorkflowSelectionGateways'), value: gateways},
      {label: this.t('HubWorkflowSelectionConnections'), value: connections}
    ];
  }

  selectedElementLabel(): string {
    if (this.isMultiSelection()) return this.t('HubWorkflowSelectionTitle');
    const node = this.selectedNode();
    if (node) return this.nodeTitle(node);
    const connection = this.selectedConnection();
    return connection ? `${this.nodeDisplayName(connection.from)} -> ${this.nodeDisplayName(connection.to)}` : '';
  }

  updateNode(): void {
    this.syncJson();
    this.emitWorkflowChange(true);
  }

  updateConnection(): void {
    const connection = this.selectedConnection();
    if (connection && connection.from === connection.to) {
      connection.to = this.nodes().find((node) => node.id !== connection.from)?.id || connection.to;
    }
    this.cleanupConnections();
    this.syncJson();
    this.emitWorkflowChange(true);
  }

  deleteSelected(): void {
    if (!this.hasSelection()) return;
    if (!confirm(this.t('HubWorkflowDeleteConfirmation'))) return;
    for (const id of this.selectedIds) this.deleteById(id);
    this.clearSelection();
    this.cleanupConnections();
    this.syncJson();
    this.emitWorkflowChange();
  }

  nodes(): WorkflowNodeRef[] {
    return [
      ...this.model.events.map((item) => ({id: item.id, kind: 'event' as const, item})),
      ...this.model.tasks.map((item) => ({id: item.id, kind: 'task' as const, item})),
      ...this.model.gateways.map((item) => ({id: item.id, kind: 'gateway' as const, item}))
    ];
  }

  trackNode(index: number, node: WorkflowNodeRef): string {
    return node.id;
  }

  trackConnection(index: number, connection: WorkflowConnection): string {
    return connection.id;
  }

  nodeBox(node: WorkflowNodeRef): WorkflowNodeBox {
    const size = node.kind === 'task' ? this.taskSize : node.kind === 'event' ? this.eventSize : this.gatewaySize;
    return {x: node.item.pos.x, y: node.item.pos.y, width: size.width, height: size.height};
  }

  nodeInnerBox(node: WorkflowNodeRef): WorkflowNodeBox {
    const box = this.nodeBox(node);
    const inset = this.nodeInnerInset;
    return {x: inset, y: inset, width: box.width - inset * 2, height: box.height - inset * 2};
  }

  nodeCenter(node: WorkflowNodeRef): WorkflowPosition {
    const box = this.nodeBox(node);
    return {x: box.x + box.width / 2, y: box.y + box.height / 2};
  }

  attachmentPoint(nodeId: string, attachment: WorkflowAttachment, tangentOffset = 0): WorkflowPosition {
    const node = this.findNode(nodeId);
    if (!node) return {x: 0, y: 0};
    const box = this.nodeBox(node);
    const offset = this.clampedEndpointOffset(node, attachment, tangentOffset);
    if (attachment === 'top') return {x: box.x + box.width / 2 + offset, y: box.y};
    if (attachment === 'right') return {x: box.x + box.width, y: box.y + box.height / 2 + offset};
    if (attachment === 'bottom') return {x: box.x + box.width / 2 + offset, y: box.y + box.height};
    return {x: box.x, y: box.y + box.height / 2 + offset};
  }

  connectionEndpointPoint(connection: WorkflowConnection | null | undefined, endpoint: WorkflowConnectionEndpoint): WorkflowPosition {
    if (!connection) return {x: 0, y: 0};
    const nodeId = endpoint === 'from' ? connection.from : connection.to;
    const attachment = endpoint === 'from' ? connection.fromAttachment : connection.toAttachment;
    return this.attachmentPoint(nodeId, attachment, this.connectionEndpointOffset(connection, endpoint));
  }

  private connectionEndpointOffset(connection: WorkflowConnection, endpoint: WorkflowConnectionEndpoint): number {
    const nodeId = endpoint === 'from' ? connection.from : connection.to;
    const attachment = endpoint === 'from' ? connection.fromAttachment : connection.toAttachment;
    const node = this.findNode(nodeId);
    if (!node) return 0;
    const endpoints = this.connectionEndpointsOnSide(nodeId, attachment);
    const index = endpoints.findIndex((item) => item.connectionId === connection.id && item.endpoint === endpoint);
    if (index < 0 || endpoints.length < 2) return 0;
    const maxOffset = this.maxEndpointOffset(node, attachment);
    if (maxOffset <= 0) return 0;
    const spacing = Math.min(this.connectionEndpointSpacing, maxOffset * 2 / (endpoints.length - 1));
    return (index - (endpoints.length - 1) / 2) * spacing;
  }

  private connectionEndpointsOnSide(nodeId: string, attachment: WorkflowAttachment): WorkflowConnectionEndpointRef[] {
    const endpoints: WorkflowConnectionEndpointRef[] = [];
    for (const connection of this.model.connections) {
      if (connection.from === nodeId && connection.fromAttachment === attachment) {
        endpoints.push({connectionId: connection.id, endpoint: 'from', axis: this.connectionEndpointPeerAxis(connection, 'from', attachment)});
      }
      if (connection.to === nodeId && connection.toAttachment === attachment) {
        endpoints.push({connectionId: connection.id, endpoint: 'to', axis: this.connectionEndpointPeerAxis(connection, 'to', attachment)});
      }
    }
    return endpoints.sort((a, b) => a.axis - b.axis || a.connectionId.localeCompare(b.connectionId) || a.endpoint.localeCompare(b.endpoint));
  }

  private connectionEndpointPeerAxis(connection: WorkflowConnection, endpoint: WorkflowConnectionEndpoint, attachment: WorkflowAttachment): number {
    const peer = this.findNode(endpoint === 'from' ? connection.to : connection.from);
    if (!peer) return 0;
    const center = this.nodeCenter(peer);
    return this.isHorizontalAttachment(attachment) ? center.y : center.x;
  }

  private clampedEndpointOffset(node: WorkflowNodeRef, attachment: WorkflowAttachment, offset: number): number {
    const maxOffset = this.maxEndpointOffset(node, attachment);
    return Math.min(maxOffset, Math.max(-maxOffset, offset));
  }

  private maxEndpointOffset(node: WorkflowNodeRef, attachment: WorkflowAttachment): number {
    const box = this.nodeBox(node);
    const sideLength = this.isHorizontalAttachment(attachment) ? box.height : box.width;
    return Math.max(0, sideLength / 2 - this.connectionEndpointPadding);
  }

  connectionGeometry(connection: WorkflowConnection): ConnectionGeometry {
    const startOffset = this.connectionEndpointOffset(connection, 'from');
    const endOffset = this.connectionEndpointOffset(connection, 'to');
    return this.geometryBetween(
      this.attachmentPoint(connection.from, connection.fromAttachment, startOffset),
      connection.fromAttachment,
      this.attachmentPoint(connection.to, connection.toAttachment, endOffset),
      connection.toAttachment,
      startOffset,
      endOffset
    );
  }

  previewConnectionGeometry(): ConnectionGeometry | null {
    const drag = this.connectionDragState;
    if (!drag) return null;
    const target = this.closestNode(drag.pointer, drag.fixedNodeId);
    if (drag.connectionId && drag.endpoint === 'from') {
      const connection = this.model.connections.find((item) => item.id === drag.connectionId);
      if (!connection) return null;
      const fixedOffset = this.connectionEndpointOffset(connection, 'to');
      const fixedPoint = this.attachmentPoint(connection.to, connection.toAttachment, fixedOffset);
      if (target) {
        const attachment = this.nearestAttachment(target, drag.pointer);
        return this.geometryBetween(this.attachmentPoint(target.id, attachment), attachment, fixedPoint, connection.toAttachment, 0, fixedOffset);
      }
      return this.geometryBetween(drag.pointer, this.directionBetween(drag.pointer, fixedPoint), fixedPoint, connection.toAttachment, 0, fixedOffset);
    }
    const connection = drag.connectionId ? this.model.connections.find((item) => item.id === drag.connectionId) : null;
    const fromOffset = connection ? this.connectionEndpointOffset(connection, 'from') : 0;
    const from = connection
      ? this.attachmentPoint(connection.from, connection.fromAttachment, fromOffset)
      : this.attachmentPoint(drag.fixedNodeId, drag.attachment);
    if (target) {
      const attachment = this.nearestAttachment(target, drag.pointer);
      return this.geometryBetween(from, drag.attachment, this.attachmentPoint(target.id, attachment), attachment, fromOffset, 0);
    }
    return this.geometryBetween(from, drag.attachment, drag.pointer, this.directionBetween(drag.pointer, from), fromOffset, 0);
  }

  connectionColor(connection: WorkflowConnection): string {
    if (connection.connectionType === 'forward') return '#18864b';
    if (connection.connectionType === 'back') return '#b42318';
    if (connection.connectionType === 'script') return '#b7791f';
    if (connection.connectionType === 'default') return '#c45a1b';
    return '#2563eb';
  }

  previewConnectionColor(): string {
    const drag = this.connectionDragState;
    if (!drag?.connectionId) return '#c45a1b';
    const connection = this.model.connections.find((item) => item.id === drag.connectionId);
    return connection ? this.connectionColor(connection) : '#c45a1b';
  }

  connectionTypeLabel(type: WorkflowConnectionType): string {
    return this.t(this.connectionTypes.find((entry) => entry.value === type)?.labelKey || type);
  }

  nodeTitle(node: WorkflowNodeRef): string {
    if (node.kind === 'task') return (node.item as WorkflowTask).name || node.id;
    if (node.kind === 'event') return this.t((node.item as WorkflowEvent).eventType === 'start' ? 'HubWorkflowStartEvent' : 'HubWorkflowEndEvent');
    return this.t('HubWorkflowGateway');
  }

  nodeSubtitle(node: WorkflowNodeRef): string {
    if (node.kind === 'task') {
      const task = node.item as WorkflowTask;
      return task.screen || task.taskType || node.id;
    }
    if (node.kind === 'event') return node.id;
    return (node.item as WorkflowGateway).type || node.id;
  }

  eventType(node: WorkflowNodeRef): 'start' | 'end' | '' {
    return node.kind === 'event' ? (node.item as WorkflowEvent).eventType : '';
  }

  nodeDisplayName(id: string): string {
    const node = this.findNode(id);
    return node ? this.nodeTitle(node) : id;
  }

  shortText(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, Math.max(0, maxLength - 3))}...`;
  }

  screenOptions(): string[] {
    return this.screens.map((screen) => screen.id);
  }

  taskScreenOptions(task: WorkflowTask): string[] {
    const options = this.screenOptions();
    return task.screen && !options.includes(task.screen) ? [task.screen, ...options] : options;
  }

  zoomIn(): void {
    this.setZoom(this.zoom + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoom - this.zoomStep);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  zoomPercent(): string {
    return `${Math.round(this.zoom * 100)}%`;
  }

  scaledCanvasStyle(): Record<string, string> {
    const size = this.canvasSize();
    return {
      width: `${Math.ceil(size.width * this.zoom)}px`,
      height: `${Math.ceil(size.height * this.zoom)}px`,
      minWidth: `${Math.ceil(size.width * this.zoom)}px`,
      minHeight: `${Math.ceil(size.height * this.zoom)}px`,
      backgroundSize: this.gridSize()
    };
  }

  downloadSvg(): void {
    const svgText = this.workflowSvgText();
    const blob = new Blob([svgText], {type: 'image/svg+xml;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'workflow.svg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  gridSize(): string {
    const size = 24 * this.zoom;
    return `${size}px ${size}px`;
  }

  canvasSize(): { width: number; height: number } {
    const nodes = this.nodes();
    const width = Math.max(900, ...nodes.map((node) => this.nodeBox(node).x + this.nodeBox(node).width + 80));
    const height = Math.max(560, ...nodes.map((node) => this.nodeBox(node).y + this.nodeBox(node).height + 80));
    return {width, height};
  }

  canvasViewBox(): string {
    const size = this.canvasSize();
    return `0 0 ${size.width} ${size.height}`;
  }

  modifiedDate(timestamp: number): Date | null {
    return timestamp ? new Date(timestamp * 1000) : null;
  }

  private continueNodeDrag(event: PointerEvent): void {
    if (!this.dragState) return;
    const primaryNode = this.findNode(this.dragState.primaryNodeId);
    if (!primaryNode) return;
    const pointer = this.pointerPosition(event);
    const primaryStart = this.dragState.startNodes[this.dragState.primaryNodeId];
    const primaryPosition = this.snapPosition(primaryNode, {
      x: Math.max(0, Math.round(primaryStart.x + pointer.x - this.dragState.startPointer.x)),
      y: Math.max(0, Math.round(primaryStart.y + pointer.y - this.dragState.startPointer.y))
    });
    const delta = {x: primaryPosition.x - primaryStart.x, y: primaryPosition.y - primaryStart.y};
    for (const id of this.dragState.nodeIds) {
      const node = this.findNode(id);
      const start = this.dragState.startNodes[id];
      if (!node || !start) continue;
      node.item.pos.x = Math.max(0, Math.round(start.x + delta.x));
      node.item.pos.y = Math.max(0, Math.round(start.y + delta.y));
    }
  }

  private finishNodeDrag(): void {
    if (!this.dragState) return;
    this.dragState = null;
    this.snapGuides = {vertical: null, horizontal: null};
    this.syncJson();
    this.emitWorkflowChange();
  }

  private continueConnectionDrag(event: PointerEvent): void {
    if (!this.connectionDragState) return;
    this.connectionDragState.pointer = this.pointerPosition(event);
  }

  private continueSelectionBox(event: PointerEvent): void {
    if (!this.selectionBoxState) return;
    this.selectionBoxState.current = this.pointerPosition(event);
    this.selectionBoxState.moved = Math.hypot(
      this.selectionBoxState.current.x - this.selectionBoxState.start.x,
      this.selectionBoxState.current.y - this.selectionBoxState.start.y
    ) > 5;
  }

  private finishSelectionBox(): void {
    const box = this.selectionBoxState;
    if (!box) return;
    this.selectionBoxState = null;
    if (!box.moved) return;
    const rect = this.rectFromPoints(box.start, box.current);
    const selected = this.nodes()
      .filter((node) => this.rectsIntersect(rect, this.nodeBox(node)))
      .map((node) => node.id);
    if (!box.additive) this.clearSelection();
    for (const id of selected) this.selectedIds.add(id);
    this.syncPrimarySelection();
  }

  private finishConnectionDrag(event: PointerEvent): void {
    if (!this.connectionDragState) return;
    const drag = this.connectionDragState;
    const pointer = this.pointerPosition(event);
    const target = this.closestNode(pointer, drag.fixedNodeId);
    this.connectionDragState = null;

    if (!target || target.id === drag.fixedNodeId) return;
    const attachment = this.nearestAttachment(target, pointer);
    if (drag.connectionId) {
      const connection = this.model.connections.find((item) => item.id === drag.connectionId);
      if (!connection) return;
      if (drag.endpoint === 'from') {
        connection.from = target.id;
        connection.fromAttachment = attachment;
      } else {
        connection.to = target.id;
        connection.toAttachment = attachment;
      }
      if (connection.from === connection.to) return;
      this.setSingleSelection(connection.id);
      this.syncJson();
      this.emitWorkflowChange();
      return;
    }

    const source = this.findNode(drag.fixedNodeId);
    if (!source) return;
    const sourceAttachment = drag.attachment;
    const connection: WorkflowConnection = {
      id: this.uniqueId('connection'),
      from: source.id,
      to: target.id,
      fromAttachment: sourceAttachment,
      toAttachment: attachment,
      connectionType: 'default',
      connectionScript: '',
      enabledCondition: ''
    };
    this.model.connections.push(connection);
    this.setSingleSelection(connection.id);
    this.syncJson();
    this.emitWorkflowChange();
  }

  private parseWorkflowJson(json: string, mode: WorkflowMode = 'gui'): void {
    try {
      const decoded = JSON.parse(json || this.defaultWorkflowJson()) as unknown;
      this.model = this.normalizeWorkflow(decoded);
      this.cleanupConnections();
      this.parseError = null;
      this.visualReady = true;
      this.mode = mode;
      this.rawDirty = false;
      for (const id of [...this.selectedIds]) {
        if (!this.elementExists(id)) this.selectedIds.delete(id);
      }
      this.syncPrimarySelection();
    } catch (error) {
      this.model = this.emptyModel();
      this.parseError = this.jsonEditorError(error, json);
      this.visualReady = false;
      this.mode = 'json';
      this.rawDirty = false;
      this.clearSelection();
    }
  }

  private normalizeWorkflow(value: unknown): WorkflowModel {
    const workflow = this.recordValue(value, 'workflow');
    return {
      tasks: this.arrayValue(workflow['tasks']).map((task, index) => this.normalizeTask(task, `tasks[${index}]`)),
      events: this.arrayValue(workflow['events']).map((event, index) => this.normalizeEvent(event, `events[${index}]`)),
      gateways: this.arrayValue(workflow['gateways']).map((gateway, index) => this.normalizeGateway(gateway, `gateways[${index}]`)),
      connections: this.arrayValue(workflow['connections']).map((connection, index) => this.normalizeConnection(connection, `connections[${index}]`))
    };
  }

  private normalizeTask(value: unknown, path: string): WorkflowTask {
    const task = this.recordValue(value, path);
    return {
      id: this.requiredString(task['id'], `${path}.id`),
      name: this.stringValue(task['name'], ''),
      progress: this.stringValue(task['progress'], '0'),
      taskType: this.stringValue(task['taskType'], 'php'),
      screen: this.stringValue(task['screen'], ''),
      pos: this.normalizePosition(task['pos'], `${path}.pos`)
    };
  }

  private normalizeEvent(value: unknown, path: string): WorkflowEvent {
    const event = this.recordValue(value, path);
    return {
      id: this.requiredString(event['id'], `${path}.id`),
      eventType: event['eventType'] === 'end' ? 'end' : 'start',
      pos: this.normalizePosition(event['pos'], `${path}.pos`)
    };
  }

  private normalizeGateway(value: unknown, path: string): WorkflowGateway {
    const gateway = this.recordValue(value, path);
    return {
      id: this.requiredString(gateway['id'], `${path}.id`),
      type: this.stringValue(gateway['type'], 'exclusive'),
      pos: this.normalizePosition(gateway['pos'], `${path}.pos`)
    };
  }

  private normalizeConnection(value: unknown, path: string): WorkflowConnection {
    const connection = this.recordValue(value, path);
    return {
      id: this.requiredString(connection['id'], `${path}.id`),
      from: this.requiredString(connection['from'], `${path}.from`),
      to: this.requiredString(connection['to'], `${path}.to`),
      fromAttachment: this.attachmentValue(connection['fromAttachment'], 'right'),
      toAttachment: this.attachmentValue(connection['toAttachment'], 'left'),
      connectionType: this.connectionTypeValue(connection['connectionType'], 'default'),
      connectionScript: this.stringValue(connection['connectionScript'], ''),
      enabledCondition: this.stringValue(connection['enabledCondition'], '')
    };
  }

  private normalizePosition(value: unknown, path: string): WorkflowPosition {
    const pos = this.recordValue(value, path);
    return {
      x: this.numberValue(pos['x'], 0),
      y: this.numberValue(pos['y'], 0)
    };
  }

  private syncJson(): void {
    if (!this.visualReady) return;
    this.workflow.json = JSON.stringify(this.serializeWorkflow(), null, 4);
    this.parseError = null;
    this.rawDirty = false;
  }

  private emitWorkflowChange(debounce = false): void {
    this.workflowChange.emit({json: this.workflow.json, debounce});
  }

  private serializeWorkflow(): WorkflowModel {
    return {
      tasks: this.model.tasks.map((task) => ({...task, pos: {...task.pos}})),
      events: this.model.events.map((event) => ({...event, pos: {...event.pos}})),
      gateways: this.model.gateways.map((gateway) => ({...gateway, pos: {...gateway.pos}})),
      connections: this.model.connections.map((connection) => ({...connection}))
    };
  }

  private emptyModel(): WorkflowModel {
    return {tasks: [], events: [], gateways: [], connections: []};
  }

  private defaultWorkflowJson(): string {
    return JSON.stringify(this.emptyModel(), null, 4);
  }

  private nextPosition(): WorkflowPosition {
    const count = this.nodes().length;
    return {x: 80 + (count % 4) * 220, y: 80 + Math.floor(count / 4) * 140};
  }

  private uniqueId(prefix: string): string {
    let id = `${prefix}-${Math.floor(Math.random() * 99999999) + 1}`;
    while (this.elementExists(id)) {
      id = `${prefix}-${Math.floor(Math.random() * 99999999) + 1}`;
    }
    return id;
  }

  private findNode(id: string): WorkflowNodeRef | null {
    return this.nodes().find((node) => node.id === id) || null;
  }

  private elementExists(id: string): boolean {
    return !!this.findNode(id) || this.model.connections.some((connection) => connection.id === id);
  }

  private deleteById(id: string): void {
    this.model.tasks = this.model.tasks.filter((task) => task.id !== id);
    this.model.events = this.model.events.filter((event) => event.id !== id);
    this.model.gateways = this.model.gateways.filter((gateway) => gateway.id !== id);
    this.model.connections = this.model.connections.filter((connection) => connection.id !== id && connection.from !== id && connection.to !== id);
  }

  private cleanupConnections(): void {
    const nodeIds = new Set(this.nodes().map((node) => node.id));
    this.model.connections = this.model.connections.filter((connection) => nodeIds.has(connection.from) && nodeIds.has(connection.to) && connection.from !== connection.to);
  }

  private setSingleSelection(id: string): void {
    this.selectedIds = new Set([id]);
    this.selectedId = id;
  }

  private toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
    this.syncPrimarySelection(id);
  }

  private syncPrimarySelection(preferredId = ''): void {
    if (preferredId && this.selectedIds.has(preferredId)) {
      this.selectedId = preferredId;
      return;
    }
    if (this.selectedId && this.selectedIds.has(this.selectedId)) return;
    this.selectedId = this.selectedIds.values().next().value || '';
  }

  private selectedNodeIds(): string[] {
    const nodeIds = new Set(this.nodes().map((node) => node.id));
    return [...this.selectedIds].filter((id) => nodeIds.has(id));
  }

  private rectFromPoints(start: WorkflowPosition, end: WorkflowPosition): WorkflowNodeBox {
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    return {x, y, width: Math.abs(end.x - start.x), height: Math.abs(end.y - start.y)};
  }

  private rectsIntersect(a: WorkflowNodeBox, b: WorkflowNodeBox): boolean {
    return a.x <= b.x + b.width && a.x + a.width >= b.x && a.y <= b.y + b.height && a.y + a.height >= b.y;
  }

  private setZoom(value: number): void {
    this.zoom = Math.min(this.maxZoom, Math.max(this.minZoom, Math.round(value * 10) / 10));
  }

  private workflowSvgText(): string {
    const size = this.canvasSize();
    const content = [
      '<g class="connections">',
      ...this.model.connections.map((connection) => this.connectionSvg(connection)),
      '</g>',
      '<g class="nodes">',
      ...this.nodes().map((node) => this.nodeSvg(node)),
      '</g>'
    ].join('');

    return `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" width="${size.width}" height="${size.height}" viewBox="0 0 ${size.width} ${size.height}"><style>${this.exportSvgCss()}</style>${content}</svg>`;
  }

  private connectionSvg(connection: WorkflowConnection): string {
    const geometry = this.connectionGeometry(connection);
    const color = this.connectionColor(connection);
    return `<g><path class="connection-line" d="${this.escapeAttr(geometry.path)}" stroke="${color}"></path><path class="connection-arrow-handle" d="${this.escapeAttr(geometry.arrowPath)}" stroke="${color}"></path><text class="connection-label" x="${geometry.label.x}" y="${geometry.label.y - 10}">${this.escapeText(this.connectionTypeLabel(connection.connectionType))}</text></g>`;
  }

  private nodeSvg(node: WorkflowNodeRef): string {
    const title = this.escapeText(this.shortText(this.nodeTitle(node), 22));
    const subtitle = this.escapeText(this.shortText(this.nodeSubtitle(node), 24));
    const transform = `translate(${node.item.pos.x} ${node.item.pos.y})`;
    if (node.kind === 'task') {
      return `<g class="workflow-node task-node" transform="${transform}"><rect class="node-outer-shape" width="${this.taskSize.width}" height="${this.taskSize.height}" rx="20"></rect><rect class="node-shape" x="${this.nodeInnerInset}" y="${this.nodeInnerInset}" width="${this.taskSize.width - this.nodeInnerInset * 2}" height="${this.taskSize.height - this.nodeInnerInset * 2}" rx="14"></rect><text class="node-title" x="22" y="34">${title}</text><text class="node-subtitle" x="22" y="57">${subtitle}</text></g>`;
    }
    if (node.kind === 'event') {
      const eventClass = this.eventType(node) === 'start' ? 'start-event-node' : 'end-event-node';
      return `<g class="workflow-node event-node ${eventClass}" transform="${transform}"><rect class="node-outer-shape" width="${this.eventSize.width}" height="${this.eventSize.height}" rx="12"></rect><circle class="node-shape" cx="${this.eventSize.width / 2}" cy="${this.eventSize.height / 2}" r="${this.eventSize.width / 2 - 10}"></circle><text class="event-label" x="${this.eventSize.width / 2}" y="${this.eventSize.height / 2 + 5}">${title}</text></g>`;
    }
    return `<g class="workflow-node gateway-node" transform="${transform}"><rect class="node-shape gateway-shape" x="${this.gatewaySize.width / 2 - 24}" y="${this.gatewaySize.height / 2 - 24}" width="48" height="48" transform="rotate(45 37 37)"></rect><text class="event-label" x="${this.gatewaySize.width / 2}" y="${this.gatewaySize.height / 2 + 5}">X</text></g>`;
  }

  private exportSvgCss(): string {
    return '.node-outer-shape{fill:#fff;stroke:#64748b;stroke-width:2}.node-shape{fill:#fff;stroke:#64748b;stroke-width:2}.event-node .node-shape{fill:#eef2ff}.start-event-node .node-shape{fill:#dcfce7;stroke:#18864b}.end-event-node .node-shape{fill:#fee2e2;stroke:#b42318}.gateway-node .node-shape{fill:#fff7ed}.node-title{fill:#172033;font:700 14px system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.node-subtitle,.event-label{fill:#475467;font:12px system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.event-label{text-anchor:middle;font-weight:700}.connection-line{fill:none;stroke-width:3}.connection-arrow-handle{fill:none;stroke-width:3}.connection-label{fill:#475467;font:700 11px system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;paint-order:stroke;stroke:#fff;stroke-width:4;text-anchor:middle}';
  }

  private escapeText(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private escapeAttr(value: string): string {
    return this.escapeText(value).replace(/"/g, '&quot;');
  }

  private snapPosition(node: WorkflowNodeRef, position: WorkflowPosition): WorkflowPosition {
    const box = this.nodeBox(node);
    const movingCenter = {x: position.x + box.width / 2, y: position.y + box.height / 2};
    const threshold = this.snapThresholdPixels / this.zoom;
    let snapped = {...position};
    let guides: SnapGuides = {vertical: null, horizontal: null};
    let bestX = threshold;
    let bestY = threshold;

    for (const other of this.nodes()) {
      if (other.id === node.id) continue;
      const otherCenter = this.nodeCenter(other);
      const xDistance = Math.abs(otherCenter.x - movingCenter.x);
      if (xDistance <= bestX) {
        bestX = xDistance;
        guides.vertical = otherCenter.x;
        snapped.x = Math.round(otherCenter.x - box.width / 2);
      }
      const yDistance = Math.abs(otherCenter.y - movingCenter.y);
      if (yDistance <= bestY) {
        bestY = yDistance;
        guides.horizontal = otherCenter.y;
        snapped.y = Math.round(otherCenter.y - box.height / 2);
      }
    }

    snapped.x = Math.max(0, snapped.x);
    snapped.y = Math.max(0, snapped.y);
    this.snapGuides = guides;
    return snapped;
  }

  pointerPosition(event: PointerEvent): WorkflowPosition {
    const svg = this.canvas?.nativeElement;
    if (!svg) return {x: event.clientX, y: event.clientY};
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const transform = svg.getScreenCTM();
    if (!transform) return {x: event.clientX, y: event.clientY};
    const cursor = point.matrixTransform(transform.inverse());
    return {x: cursor.x, y: cursor.y};
  }

  private closestNode(pointer: WorkflowPosition, excludeId: string): WorkflowNodeRef | null {
    let closest: WorkflowNodeRef | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const node of this.nodes()) {
      if (node.id === excludeId) continue;
      const center = this.nodeCenter(node);
      const distance = Math.hypot(center.x - pointer.x, center.y - pointer.y);
      if (distance < closestDistance) {
        closest = node;
        closestDistance = distance;
      }
    }
    return closestDistance <= 130 ? closest : null;
  }

  attachmentFromLocalPointer(node: WorkflowNodeRef, pointer: WorkflowPosition): WorkflowAttachment {
    const box = this.nodeBox(node);
    const relative = {x: pointer.x - box.x, y: pointer.y - box.y};
    if (relative.x < this.nodeInnerInset) return 'left';
    if (relative.x > box.width - this.nodeInnerInset) return 'right';
    if (relative.y < box.height / 2) return 'top';
    return 'bottom';
  }

  private nearestAttachment(node: WorkflowNodeRef, pointer: WorkflowPosition): WorkflowAttachment {
    const box = this.nodeBox(node);
    if (pointer.x >= box.x && pointer.x <= box.x + box.width && pointer.y >= box.y && pointer.y <= box.y + box.height) {
      return this.attachmentFromLocalPointer(node, pointer);
    }
    return this.directionBetween(this.nodeCenter(node), pointer);
  }

  private geometryBetween(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment, startOffset = 0, endOffset = 0): ConnectionGeometry {
    if (startAttachment === endAttachment) {
      return this.sameSideGeometry(start, startAttachment, end, endAttachment, startOffset, endOffset);
    }
    if (this.isOutwardOpposite(start, startAttachment, end, endAttachment)) {
      return this.outwardOppositeGeometry(start, startAttachment, end, endAttachment);
    }
    if (this.isPerpendicular(startAttachment, endAttachment)) {
      return this.perpendicularGeometry(start, startAttachment, end, endAttachment);
    }
    return this.oppositeGeometry(start, startAttachment, end, endAttachment);
  }

  private sameSideGeometry(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment, startOffset: number, endOffset: number): ConnectionGeometry {
    const bridge = this.sameSideBridgeDistance(startAttachment, startOffset, endOffset);
    const points = this.sameSidePoints(start, startAttachment, end, bridge);
    return {
      start,
      startControl: points[1],
      endControl: points[points.length - 2],
      end,
      path: this.roundedPath(points, 18),
      arrowPath: this.arrowHead(end, endAttachment),
      label: this.pathLabel(points)
    };
  }

  private sameSidePoints(start: WorkflowPosition, attachment: WorkflowAttachment, end: WorkflowPosition, bridge: number): WorkflowPosition[] {
    if (attachment === 'top') {
      const y = Math.min(start.y, end.y) - bridge;
      return [start, {x: start.x, y}, {x: end.x, y}, end];
    }
    if (attachment === 'bottom') {
      const y = Math.max(start.y, end.y) + bridge;
      return [start, {x: start.x, y}, {x: end.x, y}, end];
    }
    if (attachment === 'left') {
      const x = Math.min(start.x, end.x) - bridge;
      return [start, {x, y: start.y}, {x, y: end.y}, end];
    }
    const x = Math.max(start.x, end.x) + bridge;
    return [start, {x, y: start.y}, {x, y: end.y}, end];
  }

  private sameSideBridgeDistance(attachment: WorkflowAttachment, startOffset: number, endOffset: number): number {
    const base = this.isHorizontalAttachment(attachment) ? 80 : 54;
    const fanOut = Math.max(Math.abs(startOffset), Math.abs(endOffset));
    return base + fanOut;
  }

  private oppositeGeometry(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment): ConnectionGeometry {
    const points = this.isHorizontalAttachment(startAttachment)
      ? this.horizontalOppositePoints(start, end)
      : this.verticalOppositePoints(start, end);
    return {
      start,
      startControl: points[1] || start,
      endControl: points[points.length - 2] || end,
      end,
      path: this.roundedPath(points, 18),
      arrowPath: this.arrowHead(end, endAttachment),
      label: this.pathLabel(points)
    };
  }

  private horizontalOppositePoints(start: WorkflowPosition, end: WorkflowPosition): WorkflowPosition[] {
    if (Math.abs(start.y - end.y) < 1) return [start, end];
    const midX = (start.x + end.x) / 2;
    return [start, {x: midX, y: start.y}, {x: midX, y: end.y}, end];
  }

  private verticalOppositePoints(start: WorkflowPosition, end: WorkflowPosition): WorkflowPosition[] {
    if (Math.abs(start.x - end.x) < 1) return [start, end];
    const midY = (start.y + end.y) / 2;
    return [start, {x: start.x, y: midY}, {x: end.x, y: midY}, end];
  }

  private outwardOppositeGeometry(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment): ConnectionGeometry {
    const loop = 42;
    const startOuter = this.controlPoint(start, startAttachment, loop);
    const endOuter = this.controlPoint(end, endAttachment, loop);
    const points = this.outwardOppositePoints(start, startAttachment, end, startOuter, endOuter, loop);
    return {
      start,
      startControl: startOuter,
      endControl: endOuter,
      end,
      path: this.roundedPath(points, 14),
      arrowPath: this.arrowHead(end, endAttachment),
      label: this.pathLabel(points)
    };
  }

  private outwardOppositePoints(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, startOuter: WorkflowPosition, endOuter: WorkflowPosition, turnDistance: number): WorkflowPosition[] {
    const turn = this.loopTurnAttachment(startAttachment, start, end);
    if (this.isHorizontalAttachment(startAttachment)) {
      const turnSign = turn === 'top' ? -1 : 1;
      const routeY = startOuter.y + turnSign * turnDistance;
      return [start, startOuter, {x: startOuter.x, y: routeY}, {x: endOuter.x, y: routeY}, endOuter, end];
    }

    const turnSign = turn === 'left' ? -1 : 1;
    const routeX = startOuter.x + turnSign * turnDistance;
    return [start, startOuter, {x: routeX, y: startOuter.y}, {x: routeX, y: endOuter.y}, endOuter, end];
  }

  private perpendicularGeometry(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment): ConnectionGeometry {
    const bend = this.isHorizontalAttachment(startAttachment) ? {x: end.x, y: start.y} : {x: start.x, y: end.y};
    if (this.pointIsOutsideAttachment(start, startAttachment, bend) && this.pointIsOutsideAttachment(end, endAttachment, bend)) {
      return {
        start,
        startControl: bend,
        endControl: bend,
        end,
        path: this.roundedPath([start, bend, end], 18),
        arrowPath: this.arrowHead(end, endAttachment),
        label: this.pathLabel([start, bend, end])
      };
    }

    const offset = 46;
    const startOuter = this.controlPoint(start, startAttachment, offset);
    const endOuter = this.controlPoint(end, endAttachment, offset);
    const mid = this.isHorizontalAttachment(startAttachment)
      ? {x: startOuter.x, y: endOuter.y}
      : {x: endOuter.x, y: startOuter.y};
    const points = [start, startOuter, mid, endOuter, end];
    return {
      start,
      startControl: startOuter,
      endControl: endOuter,
      end,
      path: this.roundedPath(points, 16),
      arrowPath: this.arrowHead(end, endAttachment),
      label: this.pathLabel(points)
    };
  }

  private roundedPath(points: WorkflowPosition[], radius: number): string {
    const route = this.compactRoutePoints(points);
    if (route.length === 0) return '';
    if (route.length === 1) return `M ${route[0].x} ${route[0].y} L ${route[0].x} ${route[0].y}`;
    const commands = [`M ${route[0].x} ${route[0].y}`];
    for (let i = 1; i < route.length - 1; i++) {
      const previous = route[i - 1];
      const current = route[i];
      const next = route[i + 1];
      const previousDistance = Math.hypot(previous.x - current.x, previous.y - current.y);
      const nextDistance = Math.hypot(next.x - current.x, next.y - current.y);
      const corner = Math.min(radius, previousDistance / 2, nextDistance / 2);
      if (corner <= 0.5) {
        commands.push(`L ${current.x} ${current.y}`);
        continue;
      }
      const before = {
        x: current.x + (previous.x - current.x) / previousDistance * corner,
        y: current.y + (previous.y - current.y) / previousDistance * corner
      };
      const after = {
        x: current.x + (next.x - current.x) / nextDistance * corner,
        y: current.y + (next.y - current.y) / nextDistance * corner
      };
      commands.push(`L ${before.x} ${before.y}`);
      commands.push(`Q ${current.x} ${current.y} ${after.x} ${after.y}`);
    }
    const last = route[route.length - 1];
    commands.push(`L ${last.x} ${last.y}`);
    return commands.join(' ');
  }

  private pathLabel(points: WorkflowPosition[]): WorkflowPosition {
    const route = this.compactRoutePoints(points);
    if (route.length === 0) return {x: 0, y: 0};
    if (route.length === 1) return route[0];
    const segments = route.slice(1).map((point, index) => ({
      start: route[index],
      end: point,
      length: Math.hypot(point.x - route[index].x, point.y - route[index].y)
    }));
    const total = segments.reduce((sum, segment) => sum + segment.length, 0);
    if (total <= 0.5) return route[0];
    let remaining = total / 2;
    for (const segment of segments) {
      if (remaining <= segment.length) {
        const ratio = segment.length === 0 ? 0 : remaining / segment.length;
        return {
          x: segment.start.x + (segment.end.x - segment.start.x) * ratio,
          y: segment.start.y + (segment.end.y - segment.start.y) * ratio
        };
      }
      remaining -= segment.length;
    }
    return route[route.length - 1];
  }

  private compactRoutePoints(points: WorkflowPosition[]): WorkflowPosition[] {
    return points.reduce<WorkflowPosition[]>((route, point) => {
      const previous = route[route.length - 1];
      if (!previous || Math.hypot(point.x - previous.x, point.y - previous.y) > 0.5) route.push(point);
      return route;
    }, []);
  }

  private controlPoint(point: WorkflowPosition, attachment: WorkflowAttachment, distance: number): WorkflowPosition {
    if (attachment === 'top') return {x: point.x, y: point.y - distance};
    if (attachment === 'right') return {x: point.x + distance, y: point.y};
    if (attachment === 'bottom') return {x: point.x, y: point.y + distance};
    return {x: point.x - distance, y: point.y};
  }

  private isOutwardOpposite(start: WorkflowPosition, startAttachment: WorkflowAttachment, end: WorkflowPosition, endAttachment: WorkflowAttachment): boolean {
    if (startAttachment === 'left' && endAttachment === 'right') return start.x < end.x;
    if (startAttachment === 'right' && endAttachment === 'left') return start.x > end.x;
    if (startAttachment === 'top' && endAttachment === 'bottom') return start.y < end.y;
    if (startAttachment === 'bottom' && endAttachment === 'top') return start.y > end.y;
    return false;
  }

  private isPerpendicular(a: WorkflowAttachment, b: WorkflowAttachment): boolean {
    return this.isHorizontalAttachment(a) !== this.isHorizontalAttachment(b);
  }

  private isHorizontalAttachment(attachment: WorkflowAttachment): boolean {
    return attachment === 'left' || attachment === 'right';
  }

  private pointIsOutsideAttachment(origin: WorkflowPosition, attachment: WorkflowAttachment, point: WorkflowPosition): boolean {
    const margin = 0.5;
    if (attachment === 'left') return point.x < origin.x - margin;
    if (attachment === 'right') return point.x > origin.x + margin;
    if (attachment === 'top') return point.y < origin.y - margin;
    return point.y > origin.y + margin;
  }

  private loopTurnAttachment(attachment: WorkflowAttachment, start: WorkflowPosition, end: WorkflowPosition): WorkflowAttachment {
    if (attachment === 'left' || attachment === 'right') {
      if (Math.abs(end.y - start.y) < 40) return 'top';
      return end.y >= start.y ? 'bottom' : 'top';
    }
    if (Math.abs(end.x - start.x) < 40) return 'left';
    return end.x >= start.x ? 'right' : 'left';
  }

  private oppositeAttachment(attachment: WorkflowAttachment): WorkflowAttachment {
    if (attachment === 'top') return 'bottom';
    if (attachment === 'bottom') return 'top';
    if (attachment === 'left') return 'right';
    return 'left';
  }

  private arrowHead(point: WorkflowPosition, attachment: WorkflowAttachment): string {
    const width = 14;
    const bend = 10;
    if (attachment === 'bottom') return `M ${point.x} ${point.y} L ${point.x + width} ${point.y + bend} M ${point.x} ${point.y} L ${point.x - width} ${point.y + bend}`;
    if (attachment === 'right') return `M ${point.x} ${point.y} L ${point.x + bend} ${point.y - width} M ${point.x} ${point.y} L ${point.x + bend} ${point.y + width}`;
    if (attachment === 'top') return `M ${point.x} ${point.y} L ${point.x + width} ${point.y - bend} M ${point.x} ${point.y} L ${point.x - width} ${point.y - bend}`;
    return `M ${point.x} ${point.y} L ${point.x - bend} ${point.y + width} M ${point.x} ${point.y} L ${point.x - bend} ${point.y - width}`;
  }

  private directionBetween(origin: WorkflowPosition, pointer: WorkflowPosition): WorkflowAttachment {
    const dx = pointer.x - origin.x;
    const dy = pointer.y - origin.y;
    if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 'right' : 'left';
    return dy > 0 ? 'bottom' : 'top';
  }

  private recordValue(value: unknown, path: string): Record<string, unknown> {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    throw new Error(this.t('HubWorkflowObjectError').replace('{path}', path));
  }

  private arrayValue(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private requiredString(value: unknown, path: string): string {
    if (typeof value === 'string' && value !== '') return value;
    throw new Error(this.t('HubWorkflowRequiredStringError').replace('{path}', path));
  }

  private stringValue(value: unknown, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
  }

  private numberValue(value: unknown, fallback: number): number {
    const numericValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }

  private attachmentValue(value: unknown, fallback: WorkflowAttachment): WorkflowAttachment {
    return value === 'top' || value === 'right' || value === 'bottom' || value === 'left' ? value : fallback;
  }

  private connectionTypeValue(value: unknown, fallback: WorkflowConnectionType): WorkflowConnectionType {
    return value === 'forward' || value === 'back' || value === 'submit' || value === 'script' || value === 'default' ? value : fallback;
  }

  jsonErrorDetails(error: JsonEditorError): string[] {
    return error.details;
  }

  private jsonEditorError(error: unknown, json: string): JsonEditorError {
    const message = error instanceof Error ? error.message : String(error);
    const details: string[] = [];
    const position = this.jsonErrorPosition(message);
    if (position !== null) {
      const location = this.jsonLineColumn(json, position);
      details.push(this.t('HubJsonErrorLocation').replace('{line}', String(location.line)).replace('{column}', String(location.column)));
    }
    details.push(message);
    return {title: this.t('HubJsonVisualParseErrorTitle'), details};
  }

  private jsonErrorPosition(message: string): number | null {
    const match = message.match(/position (\d+)/i);
    return match ? Number(match[1]) : null;
  }

  private jsonLineColumn(json: string, position: number): {line: number; column: number} {
    const beforeError = json.slice(0, Math.max(0, position));
    const lines = beforeError.split('\n');
    return {line: lines.length, column: lines[lines.length - 1].length + 1};
  }
}
